<template>
  <h3 class="text-lg mt-4">Equipment Info</h3>
  <div v-for="(item, key) in asset" :key="key" class="border-b border-primary-500 px-4 py-2.5 mb-6">
    <div class="text-slate-400 text-xs font-light capitalize">{{ key }}</div>
    <div class="text-slate-600">{{ item ?? '-' }}</div>
  </div>

  <div class="flex mt-20 gap-x-4">
    <h3 class="text-lg grow">Images</h3>
    <div class="shrink-0">
      <file-uploader multiple @uploaded="onUploaded">
        <button class="btn-primary">Add Image</button>
      </file-uploader>
    </div>
  </div>
  <images-horizontal v-if="images.length" :images="images" @delete="onFileDeleted" />
  <div v-else>
    <div class="flex items-center justify-center">
      <div class="text-sm text-slate-400">No images yet</div>
    </div>
  </div>

  <div class="flex mt-20 gap-x-4">
    <h3 class="text-lg grow">Files</h3>
    <div class="shrink-0">
      <file-uploader multiple @uploaded="onUploaded">
        <button class="btn-primary">Add File</button>
      </file-uploader>
    </div>
  </div>
  <files-horizontal v-if="files.length" :files="files" @delete="onFileDeleted" />
  <div v-else>
    <div class="flex items-center justify-center">
      <div class="text-sm text-slate-400">No files yet</div>
    </div>
  </div>

  <h3 class="text-lg mt-20">Comments</h3>
  <message-input key="main" :asset-id="assetId" class="mt-4 mb-20" @update="onCommentAdded" />
  <div v-if="comments.length">
    <item-comment v-for="(comment, key) in comments" :key="key" :comment="comment" @delete="onCommentDeleted" @edit="onCommentEdit" />
  </div>
  <div v-else>
    <div class="flex items-center justify-center">
      <div class="text-sm text-slate-400">No comments yet</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import ImagesHorizontal from './ImagesHorizontal.vue';
import FilesHorizontal from './FilesHorizontal.vue';
import MessageInput from './MessageInput.vue';
import ItemComment from './ItemComment.vue';
import FileUploader from './FileUploader.vue';
import { OverlayModal } from '../composables/useModal';
import { $get, $post, $delete } from '../composables/useApi';
import { $error } from '../composables/useNotify';

const props = defineProps({
  assetId: {
    type: String,
    default: null
  },
  asset: {
    type: Object,
    default: () => {}
  }
});

const imagesExtensions = [ 'jpg', 'jpeg', 'png', 'gif', 'webp' ];
const urls = ref([]);
const files = computed(() => urls.value.filter(url => !imagesExtensions.includes(url.url.split('.').pop())).map(url => ({
  id: url.id,
  url: url.url,
  name: url.url.split('/').pop(),
  size: 0
})));
const images = computed(() => urls.value.filter(url => imagesExtensions.includes(url.url.split('.').pop())).map(url => ({
  id: url.id,
  url: url.url,
  size: 0
})));

const comments = ref([]);

loadData();

function loadData() {
  if (!props.assetId) {
    $error('Asset ID is not defined');
    return;
  }

  loadComments();
  loadFiles();
}

async function loadComments() {
  const response = await $get(`/asset/${props.assetId?.replace('#', '')}/comments`);

  if (response?.error) {
    console.error('Error getting comments');
    return;
  }

  comments.value = response.comments.map(comment => ({
    id: comment.id,
    from: comment.from,
    createdAt: new Date(comment.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }),
    text: comment.text,
    files: comment.files.map(file => ({
      id: file,
      name: file.split('/').pop(),
      size: 0
    }))
  }));
}

async function loadFiles() {
  const response = await $get(`/asset/${props.assetId?.replace('#', '')}/files`);

  if (response?.error) {
    console.error('Error getting files');
    return;
  }

  urls.value = response.files;
}

async function onFileDeleted(id) {
  const response = await $delete(`/asset/file/${id}`);

  if (!response.success) {
    $error('Error deleting file');
    console.error('Error deleting file:', response.error);
    return;
  }

  urls.value.splice(urls.value.findIndex(file => file.id === id), 1);
}

async function onUploaded(_urls) {
  console.log('upload', _urls);

  const response = await $post(`/asset/${props.assetId.replace('#', '')}/files`, { files: _urls });

  if (!response?.success) {
    $error('Error posting comment');
    console.error('Error posting comment:', response.error);
    return;
  }

  loadFiles();
}

function onCommentAdded() {
  loadComments();
}

async function onCommentDeleted(id) {
  const response = await $delete(`/asset/comment/${id}`);

  if (!response?.success) {
    $error('Error deleting comment');
    console.error('Error deleting comment:', response.error);
    return;
  }

  comments.value.splice(comments.value.findIndex(comment => comment.id === id), 1);
}

async function onCommentEdit(id) {
  const comment = comments.value.find(_comment => _comment.id === id);
  if (!comment) {
    return;
  }

  const isSaved = await OverlayModal.dialog({
    title: 'Edit Comment',
    component: MessageInput,
    componentData: {
      comment
    }
  });

  if (!isSaved) {
    return;
  }

  loadComments();
}
</script>
